import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from 'react-oidc-context';
import {
  Alert,
  Breadcrumb,
  BreadcrumbItem,
  Button,
  Card,
  CardBody,
  Content,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateActions,
  EmptyStateBody,
  EmptyStateFooter,
  Flex,
  FlexItem,
  Icon,
  Label,
  MenuToggle,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PageBreadcrumb,
  PageSection,
  PageSectionVariants,
  Pagination,
  SearchInput,
  Tab,
  TabTitleText,
  Tabs,
  Toolbar,
  ToolbarContent,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import {
  EllipsisVIcon,
  EnvelopeIcon,
  ExclamationCircleIcon,
  PlusCircleIcon,
  SearchIcon,
} from '@patternfly/react-icons';
import CenteredSpinner from '@app/CenteredSpinner';
import Unauthenticated from '@app/Unauthenticated';
import InTransferAlert from '../Domains/InTransferAlert';
import { useApiFetch } from '../../../api';
import { useMailDomain } from '../../../hooks/useMailDomain';
import { useMailboxes } from '../../../hooks/useMailboxes';
import { useMailAliases } from '../../../hooks/useMailAliases';
import { useAntispamPlatforms } from '../../../hooks/useAntispamPlatforms';
import { useNotifications } from '../../../contexts/NotificationContext';
import { formatDateNumeric } from '../../../utils/dateFormat';
import type { ApiResponseMailDomainDelete } from '../../../types/api/ApiResponseMailDomain';
import type { Mailbox } from '../../../types/models/Mailbox';
import type { MailAlias } from '../../../types/models/MailAlias';
import MailboxCreateModal from './MailboxCreateModal';
import MailboxPasswordModal from './MailboxPasswordModal';
import MailboxDeleteModal from './MailboxDeleteModal';
import MailAliasCreateModal from './MailAliasCreateModal';
import MailAliasEditModal from './MailAliasEditModal';
import MailAliasDeleteModal from './MailAliasDeleteModal';
import { AntispamStatusLabel } from './AntispamStatusLabel';
import { EnableAntispamModal } from './EnableAntispamModal';
import { DisableAntispamModal } from './DisableAntispamModal';
import DkimTab from './DkimTab';
import SpfTab from './SpfTab';
import DmarcTab from './DmarcTab';

function EmailsDetail() {
  const { domainId } = useParams<{ domainId: string }>();
  const auth = useAuth();
  const apiFetch = useApiFetch();
  const navigate = useNavigate();
  const { mailDomain, loading, error, notFound, refetch } = useMailDomain(Number(domainId));
  const { mailboxes, loading: mbLoading, error: mbError, refetch: refetchMailboxes } = useMailboxes(mailDomain?.id);
  const { aliases, loading: aliasLoading, error: aliasError, refetch: refetchAliases } = useMailAliases(mailDomain?.id);
  const { platforms: antispamPlatforms } = useAntispamPlatforms();
  const { addNotification, setDrawerOpen } = useNotifications();

  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // Kinds the backend refuses to delete over ("mailboxes", "aliases"): they must go first.
  const [blockedBy, setBlockedBy] = useState<string[] | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isEnableOpen, setIsEnableOpen] = useState(false);
  const [isDisableOpen, setIsDisableOpen] = useState(false);

  const [activeTab, setActiveTab] = useState<string | number>(0);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [passwordTarget, setPasswordTarget] = useState<Mailbox | null>(null);
  const [deleteMailboxTarget, setDeleteMailboxTarget] = useState<Mailbox | null>(null);
  const [isKebabOpen, setIsKebabOpen] = useState(false);

  const [isAliasCreateOpen, setIsAliasCreateOpen] = useState(false);
  const [editAliasTarget, setEditAliasTarget] = useState<MailAlias | null>(null);
  const [deleteAliasTarget, setDeleteAliasTarget] = useState<MailAlias | null>(null);

  const [mailboxSearch, setMailboxSearch] = useState('');
  const [debouncedMailboxSearch, setDebouncedMailboxSearch] = useState('');
  const [mailboxPage, setMailboxPage] = useState(1);
  const [mailboxPerPage, setMailboxPerPage] = useState(25);

  const [aliasSearch, setAliasSearch] = useState('');
  const [debouncedAliasSearch, setDebouncedAliasSearch] = useState('');
  const [aliasPage, setAliasPage] = useState(1);
  const [aliasPerPage, setAliasPerPage] = useState(25);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedMailboxSearch(mailboxSearch);
      setMailboxPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [mailboxSearch]);

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedAliasSearch(aliasSearch);
      setAliasPage(1);
    }, 400);
    return () => clearTimeout(handle);
  }, [aliasSearch]);

  const filteredMailboxes = useMemo(() => {
    const q = debouncedMailboxSearch.trim().toLowerCase();
    if (!q) return mailboxes;
    return mailboxes.filter((m) => m.user.toLowerCase().includes(q));
  }, [mailboxes, debouncedMailboxSearch]);

  const paginatedMailboxes = useMemo(() => {
    const start = (mailboxPage - 1) * mailboxPerPage;
    return filteredMailboxes.slice(start, start + mailboxPerPage);
  }, [filteredMailboxes, mailboxPage, mailboxPerPage]);

  const filteredAliases = useMemo(() => {
    const q = debouncedAliasSearch.trim().toLowerCase();
    if (!q) return aliases;
    return aliases.filter((a) => a.source.toLowerCase().includes(q) || a.destination.toLowerCase().includes(q));
  }, [aliases, debouncedAliasSearch]);

  const paginatedAliases = useMemo(() => {
    const start = (aliasPage - 1) * aliasPerPage;
    return filteredAliases.slice(start, start + aliasPerPage);
  }, [filteredAliases, aliasPage, aliasPerPage]);

  if (!auth.isAuthenticated) {
    return <Unauthenticated />;
  }

  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);
    setBlockedBy(null);

    try {
      const response = await apiFetch(`/emails/domains/${domainId}`, {
        method: 'DELETE',
      });
      const body = (await response.json().catch(() => null)) as ApiResponseMailDomainDelete | null;

      if (body?.status === 'success') {
        navigate('/datacenter/emails');
        return;
      }

      // The backend refuses while the host still holds mailboxes or aliases, and names what blocks.
      const blockers = body?.data !== undefined && 'blocked_by' in body.data ? body.data.blocked_by : null;
      if (blockers !== null && blockers.length > 0) {
        setBlockedBy(blockers);
        return;
      }

      const statusText = response.statusText ? ` ${response.statusText}` : '';
      throw new Error(body?.message ?? `Failed to delete the mail domain (HTTP ${response.status}${statusText})`);
    } catch (err) {
      setDeleteError(err instanceof Error ? err.message : 'Failed to delete mail domain');
    } finally {
      setIsDeleting(false);
    }
  };

  // EnableAntispamModal now owns the success/warning notifications (only it knows whether the DNS/MX
  // steps ran), so this callback just refreshes the domain so the antispam status reflects reality.
  const handleAntispamEnabled = () => {
    void refetch();
  };

  const handleAntispamDisabled = () => {
    addNotification({
      title: 'Antispam disabled',
      srTitle: 'Success alert',
      description: `Antispam is no longer managing ${mailDomain?.organization_domain_name ?? 'this domain'}.`,
      variant: 'success',
    });
    setDrawerOpen(true);
    void refetch();
  };

  const breadcrumbName = mailDomain?.organization_domain_name ?? (loading ? '...' : 'Not found');

  const renderMailboxesTab = () => {
    if (mbLoading) {
      return <CenteredSpinner />;
    }

    if (mbError) {
      return (
        <EmptyState
          titleText="Unable to load mailboxes"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{mbError}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetchMailboxes()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (mailboxes.length === 0) {
      return (
        <EmptyState titleText="No mailboxes yet" icon={EnvelopeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>This mail domain has no mailboxes. Create one to get started.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                Create mailbox
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by address..."
                value={mailboxSearch}
                onChange={(_event, value) => setMailboxSearch(value)}
                onClear={() => setMailboxSearch('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredMailboxes.length}
                perPage={mailboxPerPage}
                page={mailboxPage}
                onSetPage={(_event, newPage) => setMailboxPage(newPage)}
                onPerPageSelect={(_event, newPerPage) => {
                  setMailboxPerPage(newPerPage);
                  setMailboxPage(1);
                }}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredMailboxes.length === 0 ? (
          <EmptyState titleText="No mailboxes match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all mailboxes.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setMailboxSearch('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Mailboxes list" variant="compact">
            <Thead>
              <Tr>
                <Th style={{ verticalAlign: 'middle' }} screenReaderText="Password actions" />
                <Th style={{ verticalAlign: 'middle' }}>Address</Th>
                <Th style={{ verticalAlign: 'middle' }} screenReaderText="Row actions" />
              </Tr>
            </Thead>
            <Tbody>
              {paginatedMailboxes.map((m) => (
                <Tr key={m.id}>
                  <Td isActionCell style={{ verticalAlign: 'middle' }}>
                    <Button variant="link" onClick={() => setPasswordTarget(m)}>
                      Change password
                    </Button>
                  </Td>
                  <Td dataLabel="Address" style={{ verticalAlign: 'middle' }}>
                    {m.user}@{mailDomain!.organization_domain_name}
                  </Td>
                  <Td isActionCell style={{ verticalAlign: 'middle' }}>
                    <Button variant="link" isDanger onClick={() => setDeleteMailboxTarget(m)}>
                      Delete
                    </Button>
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        )}

        {filteredMailboxes.length > 0 && (
          <Pagination
            itemCount={filteredMailboxes.length}
            perPage={mailboxPerPage}
            page={mailboxPage}
            onSetPage={(_event, newPage) => setMailboxPage(newPage)}
            onPerPageSelect={(_event, newPerPage) => {
              setMailboxPerPage(newPerPage);
              setMailboxPage(1);
            }}
            variant="bottom"
          />
        )}
      </>
    );
  };

  const renderAliasesTab = () => {
    if (aliasLoading) {
      return <CenteredSpinner />;
    }

    if (aliasError) {
      return (
        <EmptyState
          titleText="Unable to load aliases"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{aliasError}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" onClick={() => refetchAliases()}>
                Retry
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (aliases.length === 0) {
      return (
        <EmptyState titleText="No aliases yet" icon={EnvelopeIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>This mail domain has no aliases. Create one to get started.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsAliasCreateOpen(true)}>
                Create alias
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        <Toolbar>
          <ToolbarContent>
            <ToolbarItem>
              <SearchInput
                placeholder="Search by address or destination..."
                value={aliasSearch}
                onChange={(_event, value) => setAliasSearch(value)}
                onClear={() => setAliasSearch('')}
              />
            </ToolbarItem>
            <ToolbarItem variant="pagination">
              <Pagination
                itemCount={filteredAliases.length}
                perPage={aliasPerPage}
                page={aliasPage}
                onSetPage={(_event, newPage) => setAliasPage(newPage)}
                onPerPageSelect={(_event, newPerPage) => {
                  setAliasPerPage(newPerPage);
                  setAliasPage(1);
                }}
              />
            </ToolbarItem>
          </ToolbarContent>
        </Toolbar>

        {filteredAliases.length === 0 ? (
          <EmptyState titleText="No aliases match your search" icon={SearchIcon} variant="lg" headingLevel="h2">
            <EmptyStateBody>Try a different search term, or clear the filter to see all aliases.</EmptyStateBody>
            <EmptyStateFooter>
              <EmptyStateActions>
                <Button variant="primary" onClick={() => setAliasSearch('')}>
                  Clear filter
                </Button>
              </EmptyStateActions>
            </EmptyStateFooter>
          </EmptyState>
        ) : (
          <Table aria-label="Aliases list" variant="compact">
            <Thead>
              <Tr>
                <Th style={{ verticalAlign: 'middle' }} screenReaderText="Edit action" />
                <Th style={{ verticalAlign: 'middle' }}>Address</Th>
                <Th style={{ verticalAlign: 'middle' }}>Destination</Th>
                <Th style={{ verticalAlign: 'middle' }} screenReaderText="Delete action" />
              </Tr>
            </Thead>
            <Tbody>
              {paginatedAliases.map((a) => {
                const segments = a.destination
                  .split(',')
                  .map((s) => s.trim())
                  .filter((s) => s.length > 0);
                return (
                  <Tr key={a.id}>
                    <Td isActionCell style={{ verticalAlign: 'middle' }}>
                      <Button variant="link" onClick={() => setEditAliasTarget(a)}>
                        Edit
                      </Button>
                    </Td>
                    <Td dataLabel="Address" style={{ verticalAlign: 'middle' }}>
                      {a.source === '@' ? '' : a.source}@{mailDomain!.organization_domain_name}
                    </Td>
                    <Td dataLabel="Destination" style={{ verticalAlign: 'middle' }}>
                      {segments.length > 1 ? (
                        <Tooltip content={segments.join(', ')}>
                          <Label color="grey">{segments.length} recipients</Label>
                        </Tooltip>
                      ) : (
                        a.destination
                      )}
                    </Td>
                    <Td isActionCell style={{ verticalAlign: 'middle' }}>
                      <Button variant="link" isDanger onClick={() => setDeleteAliasTarget(a)}>
                        Delete
                      </Button>
                    </Td>
                  </Tr>
                );
              })}
            </Tbody>
          </Table>
        )}

        {filteredAliases.length > 0 && (
          <Pagination
            itemCount={filteredAliases.length}
            perPage={aliasPerPage}
            page={aliasPage}
            onSetPage={(_event, newPage) => setAliasPage(newPage)}
            onPerPageSelect={(_event, newPerPage) => {
              setAliasPerPage(newPerPage);
              setAliasPage(1);
            }}
            variant="bottom"
          />
        )}
      </>
    );
  };

  const renderContent = () => {
    if (loading) {
      return <CenteredSpinner />;
    }

    if (notFound) {
      return (
        <EmptyState titleText="Mail domain not found" icon={ExclamationCircleIcon} variant="lg" headingLevel="h2">
          <EmptyStateBody>The requested mail domain does not exist.</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/emails" />}>
                Back to list
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    if (error || !mailDomain) {
      return (
        <EmptyState
          titleText="Unable to load mail domain"
          icon={ExclamationCircleIcon}
          variant="lg"
          headingLevel="h2"
          status="danger"
        >
          <EmptyStateBody>{error ?? 'An unexpected error occurred.'}</EmptyStateBody>
          <EmptyStateFooter>
            <EmptyStateActions>
              <Button variant="primary" component={(props) => <Link {...props} to="/datacenter/emails" />}>
                Back to list
              </Button>
            </EmptyStateActions>
          </EmptyStateFooter>
        </EmptyState>
      );
    }

    return (
      <>
        {mailDomain.domain_in_transfer && (
          <InTransferAlert variant="mail" deadline={mailDomain.domain_transfer_expires_at} className="pf-v6-u-mb-md" />
        )}
        <Card>
          <CardBody>
            <DescriptionList columnModifier={{ default: '3Col' }}>
              <DescriptionListGroup>
                <DescriptionListTerm>Organization domain</DescriptionListTerm>
                <DescriptionListDescription>{mailDomain.organization_domain_name}</DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>IMAP server</DescriptionListTerm>
                <DescriptionListDescription>
                  {mailDomain.imap_server_host ?? 'Stale binding — please recreate'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Mode</DescriptionListTerm>
                <DescriptionListDescription>
                  {mailDomain.imap_server_mode === 'shared' && <Label color="blue">Shared</Label>}
                  {mailDomain.imap_server_mode === 'dedicated' && <Label color="green">Dedicated</Label>}
                  {mailDomain.imap_server_mode === null && '—'}
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Antispam</DescriptionListTerm>
                <DescriptionListDescription>
                  <AntispamStatusLabel platformId={mailDomain.antispam_platform_id} platforms={antispamPlatforms} />
                </DescriptionListDescription>
              </DescriptionListGroup>
              <DescriptionListGroup>
                <DescriptionListTerm>Invoicing</DescriptionListTerm>
                <DescriptionListDescription>{formatDateNumeric(mailDomain.next_billing_at)}</DescriptionListDescription>
              </DescriptionListGroup>
            </DescriptionList>
          </CardBody>
        </Card>

        <div className="pf-v6-u-mt-lg">
          <Tabs activeKey={activeTab} onSelect={(_event, tabKey) => setActiveTab(tabKey)} mountOnEnter>
            <Tab eventKey={2} title={<TabTitleText>DKIM</TabTitleText>}>
              <div className="pf-v6-u-pt-md">
                <DkimTab
                  mailDomainId={mailDomain.id}
                  organizationDomainId={mailDomain.organization_domain_id}
                  domainName={mailDomain.organization_domain_name}
                />
              </div>
            </Tab>
            <Tab eventKey={3} title={<TabTitleText>SPF</TabTitleText>}>
              <div className="pf-v6-u-pt-md">
                <SpfTab
                  mailDomainId={mailDomain.id}
                  organizationDomainId={mailDomain.organization_domain_id}
                  domainName={mailDomain.organization_domain_name}
                />
              </div>
            </Tab>
            <Tab eventKey={4} title={<TabTitleText>DMARC</TabTitleText>}>
              <div className="pf-v6-u-pt-md">
                <DmarcTab
                  mailDomainId={mailDomain.id}
                  organizationDomainId={mailDomain.organization_domain_id}
                  domainName={mailDomain.organization_domain_name}
                />
              </div>
            </Tab>
            <Tab eventKey={0} title={<TabTitleText>Mailboxes</TabTitleText>}>
              <div className="pf-v6-u-pt-md">{renderMailboxesTab()}</div>
            </Tab>
            <Tab eventKey={1} title={<TabTitleText>Aliases</TabTitleText>}>
              <div className="pf-v6-u-pt-md">{renderAliasesTab()}</div>
            </Tab>
          </Tabs>
        </div>
      </>
    );
  };

  return (
    <>
      <PageBreadcrumb hasBodyWrapper={false} hasShadowBottom={false}>
        <Breadcrumb ouiaId="BasicBreadcrumb">
          <BreadcrumbItem to="/datacenter">Datacenter</BreadcrumbItem>
          <BreadcrumbItem to="/datacenter/emails">Emails</BreadcrumbItem>
          <BreadcrumbItem isActive>{breadcrumbName}</BreadcrumbItem>
        </Breadcrumb>
      </PageBreadcrumb>
      <PageSection hasBodyWrapper variant={PageSectionVariants.default} isWidthLimited hasShadowBottom={true}>
        <Flex
          justifyContent={{ default: 'justifyContentSpaceBetween' }}
          alignItems={{ default: 'alignItemsFlexStart' }}
        >
          <FlexItem>
            <Content component="h1">
              <Icon size="lg" style={{ marginRight: '8px' }}>
                <EnvelopeIcon />
              </Icon>
              {mailDomain?.organization_domain_name ?? 'Mail domain'}
            </Content>
            <Content component="p">Mail configuration</Content>
          </FlexItem>
          {!loading && !error && !notFound && mailDomain && (
            <FlexItem>
              <Flex gap={{ default: 'gapSm' }} alignItems={{ default: 'alignItemsCenter' }}>
                {/* The Create action is per-tab and only meaningful on Mailboxes/Aliases; the DKIM and SPF
                    tabs own their primary action (Create/Build) in their own in-tab toolbar. */}
                {(activeTab === 0 || activeTab === 1) && (
                  <FlexItem>
                    {activeTab === 1 ? (
                      <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsAliasCreateOpen(true)}>
                        Create alias
                      </Button>
                    ) : (
                      <Button variant="primary" icon={<PlusCircleIcon />} onClick={() => setIsCreateOpen(true)}>
                        Create mailbox
                      </Button>
                    )}
                  </FlexItem>
                )}
                <FlexItem>
                  <Dropdown
                    isOpen={isKebabOpen}
                    onOpenChange={setIsKebabOpen}
                    onSelect={() => setIsKebabOpen(false)}
                    popperProps={{ position: 'right' }}
                    toggle={(toggleRef) => (
                      <MenuToggle
                        ref={toggleRef}
                        variant="plain"
                        onClick={() => setIsKebabOpen(!isKebabOpen)}
                        isExpanded={isKebabOpen}
                        aria-label="Mail domain actions"
                      >
                        <EllipsisVIcon />
                      </MenuToggle>
                    )}
                  >
                    <DropdownList>
                      {mailDomain.antispam_platform_id === null && (
                        <DropdownItem
                          isAriaDisabled={antispamPlatforms.length === 0}
                          tooltipProps={
                            antispamPlatforms.length === 0
                              ? { content: 'No antispam platforms are available to enable.' }
                              : undefined
                          }
                          onClick={() => {
                            if (antispamPlatforms.length > 0) {
                              setIsEnableOpen(true);
                            }
                          }}
                        >
                          Enable antispam…
                        </DropdownItem>
                      )}
                      {mailDomain.antispam_platform_id !== null && (
                        <DropdownItem onClick={() => setIsDisableOpen(true)}>Disable antispam</DropdownItem>
                      )}
                      <DropdownItem isDanger onClick={() => setIsDeleteOpen(true)}>
                        Delete mail domain
                      </DropdownItem>
                    </DropdownList>
                  </Dropdown>
                </FlexItem>
              </Flex>
            </FlexItem>
          )}
        </Flex>
      </PageSection>
      <PageSection hasBodyWrapper={false}>{renderContent()}</PageSection>

      <Modal
        variant="small"
        isOpen={isDeleteOpen}
        onClose={() => {
          setIsDeleteOpen(false);
          setDeleteError(null);
          setBlockedBy(null);
        }}
        aria-label="Delete mail domain"
      >
        <ModalHeader title="Delete mail domain" titleIconVariant="warning" />
        <ModalBody>
          {deleteError && (
            <Alert variant="danger" isInline title="Delete failed" className="pf-v6-u-mb-md">
              {deleteError}
            </Alert>
          )}
          {blockedBy !== null && (
            <Alert variant="warning" isInline title="Remove the mailboxes and aliases first" className="pf-v6-u-mb-md">
              This domain still has {blockedBy.join(' and ')} on its mail host. Delete those first, then delete the mail
              domain. Nothing has been deleted.
            </Alert>
          )}
          <Content>
            Delete mail configuration for <strong>{mailDomain?.organization_domain_name}</strong>? This removes the mail
            domain together with what it holds on its mail host: its DKIM keys, its antispam link, and the domain itself
            on the IMAP server. It only works once every mailbox and alias is gone, and it cannot be undone.
          </Content>
        </ModalBody>
        <ModalFooter>
          <Button variant="danger" onClick={handleDelete} isLoading={isDeleting} isDisabled={isDeleting}>
            Delete
          </Button>
          <Button
            variant="link"
            onClick={() => {
              setIsDeleteOpen(false);
              setDeleteError(null);
            }}
          >
            Cancel
          </Button>
        </ModalFooter>
      </Modal>

      {mailDomain && (
        <>
          <MailboxCreateModal
            isOpen={isCreateOpen}
            onClose={() => setIsCreateOpen(false)}
            mailDomainId={mailDomain.id}
            domainName={mailDomain.organization_domain_name}
            nextBillingAt={mailDomain.next_billing_at}
            imapServerId={mailDomain.netbox_imap_server_id}
            domainInTransfer={mailDomain.domain_in_transfer}
            transferDeadline={mailDomain.domain_transfer_expires_at}
            onSuccess={refetchMailboxes}
          />
          <MailboxPasswordModal
            mailbox={passwordTarget}
            onClose={() => setPasswordTarget(null)}
            domainName={mailDomain.organization_domain_name}
            mailDomainId={mailDomain.id}
          />
          <MailboxDeleteModal
            mailbox={deleteMailboxTarget}
            onClose={() => setDeleteMailboxTarget(null)}
            domainName={mailDomain.organization_domain_name}
            mailDomainId={mailDomain.id}
            onSuccess={refetchMailboxes}
          />
          <MailAliasCreateModal
            isOpen={isAliasCreateOpen}
            onClose={() => setIsAliasCreateOpen(false)}
            mailDomainId={mailDomain.id}
            domainName={mailDomain.organization_domain_name}
            onSuccess={refetchAliases}
          />
          <MailAliasEditModal
            alias={editAliasTarget}
            onClose={() => setEditAliasTarget(null)}
            mailDomainId={mailDomain.id}
            domainName={mailDomain.organization_domain_name}
            onSuccess={refetchAliases}
          />
          <MailAliasDeleteModal
            alias={deleteAliasTarget}
            onClose={() => setDeleteAliasTarget(null)}
            mailDomainId={mailDomain.id}
            domainName={mailDomain.organization_domain_name}
            onSuccess={refetchAliases}
          />
          <EnableAntispamModal
            domainId={mailDomain.id}
            organizationDomainId={mailDomain.organization_domain_id}
            platforms={antispamPlatforms}
            isOpen={isEnableOpen}
            onClose={() => setIsEnableOpen(false)}
            onSuccess={handleAntispamEnabled}
          />
          <DisableAntispamModal
            domainId={mailDomain.id}
            platformName={antispamPlatforms.find((p) => p.id === mailDomain.antispam_platform_id)?.name ?? null}
            isOpen={isDisableOpen}
            onClose={() => setIsDisableOpen(false)}
            onSuccess={handleAntispamDisabled}
          />
        </>
      )}
    </>
  );
}

export default EmailsDetail;
